import * as XLSX from 'xlsx';
import { collapseBudgetVsActualMonths, collapseMonthsToTerm, getBudgetPeriods } from './budgetDataHelpers';

export function formatBalanceSheetDate(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${value}T00:00:00Z`)
  );
}

export function safeExportName(value) {
  return String(value || 'report').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '');
}

export function downloadSpreadsheet(sheetName, rows, filename) {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet['!cols'] = rows[0].map((_, columnIndex) => ({
    wch: Math.min(40, Math.max(14, ...rows.map(row => String(row[columnIndex] ?? '').length + 2))),
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

export function buildBudgetDetailsExportRows({ info, groups, periods, grandMonths, grandTotal, budgetTermId }) {
  const toPeriods = months => collapseMonthsToTerm(months, budgetTermId);
  const rows = [
    ['Budget Details'],
    ['Budget Name', info?.budget_name || ''],
    ['Financial Year', info?.financial_year_name || info?.financial_year_code || ''],
    ['Budget Period', info?.budget_term_name || ''],
    [],
    ['Account', ...periods.map(period => period.label), 'Total'],
  ];
  groups.forEach(group => {
    rows.push([group.name]);
    group.accounts.forEach(account => rows.push([account.account_name, ...toPeriods(account.months), account.total]));
    rows.push([`Total for ${group.name}`, ...toPeriods(group.months), group.total]);
  });
  rows.push(['Total', ...toPeriods(grandMonths), grandTotal]);
  return rows;
}

export function buildProfitLossExportRows(groups, budgetTermId, financialYearCode) {
  const periods = getBudgetPeriods(budgetTermId);
  const header = ['Account'];
  periods.forEach(period => {
    const label = financialYearCode ? `${period.label} ${financialYearCode}` : period.label;
    header.push(`${label} Budget`, `${label} Actual`, `${label} Over Budget`);
  });
  const rows = [['Profit and Loss'], [], header];
  groups.forEach(group => {
    rows.push([group.name]);
    group.accounts.forEach(account => {
      const values = [account.account_name];
      collapseBudgetVsActualMonths(account.canonicalMonths || account.months, budgetTermId).forEach(period => {
        values.push(period.budget, period.actual, period.actual - period.budget);
      });
      rows.push(values);
    });
  });
  return rows;
}

export function buildBalanceSheetExportRows(groups, date) {
  const rows = [['Balance Sheet'], ['As of', formatBalanceSheetDate(date)], [], ['Account', 'Total']];
  groups.forEach(group => {
    rows.push([group.name]);
    group.mains.forEach(main => {
      rows.push([`  ${main.name}`]);
      main.types.forEach(type => {
        rows.push([`    ${type.name}`]);
        type.accounts.forEach(account => rows.push([`      ${account.name}`, account.amount]));
        rows.push([`    Total for ${type.name}`, type.total]);
      });
      rows.push([`  Total for ${main.name}`, main.total]);
    });
    rows.push([`Total for ${group.name}`, group.total]);
  });
  const liabilitiesAndEquitiesTotal = groups
    .filter(group => /liabilit|equit/i.test(group.name))
    .reduce((sum, group) => sum + group.total, 0);
  rows.push(['Total for Liabilities & Equities', liabilitiesAndEquitiesTotal]);
  return rows;
}
